import { useCallback, useMemo, useState } from 'react'
import { useEffect } from 'react'

import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Loader } from 'core-components/library/loader'
import { BaseCrud, Logic, Operator, Static } from 'core-components/types'
import { useMeasureController } from 'src/core/analytics/useMeasureController'
import { proxy } from 'src/core/classes/helpers/UrlHelper'
import { Flags } from 'src/core/imgStore'

import { configuration } from '../config'
import { LANGUAGES } from '../constants'
import { getUserId, getUserLanguage, setUserId } from '../helpers/helper'
import { IQuestionData, ISurveyContent, ITranslations } from '../models/ISurvey'
import styles from '../Survey.module.scss'

import { SURVEY_FORM, TRANSLATION_FORM } from './constants/form'
import { EndTemplate } from './EndTemplate'
import { fetchSearch, fetchSurvey } from './helpers/fetchSearch'
import { Questions } from './Questions'

export const SurveyContent = ({ onFinished, isr, isDone }: ISurveyContent) => {
  const { startLoadData, stopLoadData } = useMeasureController('spoc-survey')
  const config = configuration()

  const [loading, setLoading] = useState<boolean>(false)

  const [survey, setSurvey] = useState<IQuestionData[]>(
    config.questions.map((item) => {
      return {
        QuestionKey: item.key,
        AnswerKey: null,
        Required: item.required,
        Elaboration: '',
      }
    })
  )

  const [language, setLanguage] = useState<string>(LANGUAGES[0].code)
  const [data, setData] = useState<ITranslations[]>([])
  const [isValidAnswers, setIsValidAnswers] = useState<boolean>(true)

  const langCrud = useMemo(() => {
    return new BaseCrud({
      fetcher: new Static(LANGUAGES),
      defaultPaging: {
        page: 0,
        pageSize: 100,
      },
      keyField: 'code',
    })
  }, [])

  const isButtonDisable = useMemo(() => {
    const notAnswered = survey.some((el) => {
      const questionInfo = config.questions.find((question) => question.key === el.QuestionKey)
      if (questionInfo) {
        return questionInfo.required ? el.AnswerKey === null : false
      }
      return false
    })
    return notAnswered || !isValidAnswers
  }, [config.questions, isValidAnswers, survey])

  const translations = useMemo(() => {
    if (data.length) {
      return new Map(data.map((el) => [el.TranslationKey, el.Translation]))
    }
    return null
  }, [data])

  const fetchData = useCallback(async () => {
    startLoadData()
    setLoading(true)

    const loadTranslations = async () => {
      const payload = {
        applyAudienceFilter: false,
        from: 0,
        to: 10000,
        filter: {
          logic: Logic.AND,
          filters: [
            {
              operator: Operator.EQUAL,
              field: 'LanguageCode',
              value: language,
            },
          ],
        },
      }

      return fetchSearch(TRANSLATION_FORM.id, TRANSLATION_FORM.code, payload)
    }

    const loadSurvey = async () => {
      return fetchSurvey(SURVEY_FORM.id, SURVEY_FORM.code, getUserId())
    }

    const [result, translationsData] = await Promise.all([loadSurvey(), loadTranslations()])

    if (result?.items && result.items[0]) {
      onFinished()
    }
    setData(translationsData.items)

    setLoading(false)
    stopLoadData()
  }, [startLoadData, stopLoadData, language, onFinished])

  const getFlag = () => {
    const item = LANGUAGES.find((item) => item.code === language)
    if (item?.flag) {
      return Flags[item.flag]
    }
    return null
  }

  const changeAnswersValid = useCallback((value: boolean) => {
    setIsValidAnswers(value)
  }, [])

  const save = async () => {
    if (survey) {
      setLoading(true)

      const newSurvey = survey
        .filter((el) => el.AnswerKey !== null)
        .map((item) => {
          const currentAnswer = config.answers.find(
            (answerItem) => answerItem.key === item.AnswerKey
          )
          const currentQuestion = config.questions.find(
            (questionItem) => questionItem.key === item.QuestionKey
          )
          return {
            QuestionKey: item.QuestionKey,
            AnswerKey: item.AnswerKey,
            Elaboration: item.Elaboration,
            QuestionTitle: translations?.get(currentQuestion?.translationKey || ''),
            AnswerTitle: translations?.get(currentAnswer?.translationKey || ''),
            ISR: isr || '',
            UserId: getUserId() || '',
          }
        })

      const saveModels = async () => {
        const payload = new FormData()
        payload.append('models', JSON.stringify(newSurvey))

        return fetch(
          proxy(`/form/data-set-provider/v3/${SURVEY_FORM.id}/${SURVEY_FORM.code}/kendo/data`),
          {
            method: 'POST',
            body: payload,
          }
        )
      }

      await saveModels()
      onFinished()
      setLoading(false)
    }
  }

  const onChange = (qustionKey: number, answerKey: number, elaboration: string) => {
    const newSurvey = [...survey]
    const index = survey.findIndex((element) => element.QuestionKey === qustionKey)
    newSurvey[index].AnswerKey = answerKey
    newSurvey[index].Elaboration = elaboration

    setSurvey(newSurvey)
  }

  useEffect(() => {
    const userId = getUserId()

    if (!userId) {
      setUserId()
    }

    const userLanguage = getUserLanguage()
    const item = LANGUAGES.find(
      (el) => el.code === userLanguage || el.code.startsWith(userLanguage)
    )
    if (item) {
      setLanguage(item.code)
    }
    fetchData()
  }, [fetchData, isr])

  if (isDone && translations) {
    return <EndTemplate translations={translations} />
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div className={styles.right}>
      <Dropdown
        classes={{
          controlClasses: {
            root: styles.dropdown,
          },
        }}
        source={langCrud}
        caption={'name'}
        value={language}
        label={'Select Language'}
        onChange={(value) => value && setLanguage(value)}
        margin={'xs'}
        startContent={<img className={styles.dropdown__item_flag} src={getFlag()} alt="" />}
        renderFunction={(item) => {
          return (
            <div className={styles.dropdown__item}>
              <img className={styles.dropdown__item_flag} src={Flags[item.flag]} alt="" />
              <span className={styles.dropdown__item_name}>{item.name}</span>
            </div>
          )
        }}
      />
      {translations && survey && (
        <Questions
          survey={survey}
          translations={translations}
          onChange={onChange}
          changeAnswersValid={changeAnswersValid}
        />
      )}
      <Button
        loading={loading}
        disabled={isButtonDisable}
        className={styles.right__button}
        icon={'kbase kbase-send'}
        onClick={save}
      >
        Send
      </Button>
    </div>
  )
}
